import React, { useEffect, useState } from 'react';
import { FaArrowRight, FaCheckCircle, FaGoogle, FaPlayCircle, FaQuoteLeft, FaStar, FaTimes } from 'react-icons/fa';
import { trackSignupComplete, trackSignupFree, trackStartFree } from '../utils/analytics.js';
import { setPageSEO } from '../utils/seo.js';
import './Signup.css';

const signupUrl = 'https://app.clientalio.com/signup';

function Signup() {
  const [openFaq, setOpenFaq] = useState(0);
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  useEffect(() => setPageSEO({
    title: 'Sign Up for Clientalio - Start Building Trust Today',
    description: 'Create your free Clientalio account and turn customer feedback into trust, credibility, and stronger website conversions in minutes.',
    canonical: 'https://clientalio.com/signup',
    ogTitle: 'Sign Up for Clientalio - Start Building Trust Today',
    ogDescription: 'Create a free Clientalio account and make customer love visible before your next buyer decides.',
    twitterTitle: 'Sign Up for Clientalio - Start Building Trust Today',
    twitterDescription: 'Start free with Clientalio and turn customer feedback into your biggest marketing asset.'
  }), []);

  const benefits = [
    'Create your branded trust page in less than 2 minutes',
    'Turn customer feedback into proof buyers can believe',
    'Share one professional link instead of chasing replies manually',
    'Make customer love visible on your website without code'
  ];

  const features = [
    {
      title: 'Google signup',
      description: 'Create your workspace quickly and start building credibility before your next sales conversation.'
    },
    {
      title: 'Branded trust pages',
      description: 'Add your logo, message, and questions so every customer request feels polished, credible, and easy to answer.'
    },
    {
      title: 'Conversion-ready proof',
      description: 'Place approved customer love where buyers hesitate, so your website answers doubt before a sales call.'
    },
    {
      title: 'Reusable marketing assets',
      description: 'Approve, organize, and reuse customer stories across campaigns, landing pages, and sales conversations.'
    }
  ];

  const testimonials = [
    {
      name: 'Kunwar Ateeq Khan',
      role: 'Business Owner',
      quote: 'Clientalio helped us turn customer feedback into trust signals we can use before every sales conversation.'
    },
    {
      name: 'Priya Menon',
      role: 'Founder',
      quote: 'We started getting better replies from clients once our request looked professional, simple, and worth answering.'
    },
    {
      name: 'Amit Verma',
      role: 'Agency Owner',
      quote: 'Our best customer stories no longer disappear in WhatsApp chats. We can turn them into marketing assets every week.'
    }
  ];

  const pricing = [
    {
      id: 'basic',
      name: 'Basic',
      priceLabel: 'Free',
      period: '/Forever',
      description: 'Start turning customer love into trust today:',
      buttonClass: 'basic',
      features: [
        { text: '15 testimonials (video, text, image)' },
        { text: '1 video testimonial (720p/2 mins)' },
        { text: '1 Video Collection form + share link' },
        { text: '2 Text Collection form + share link' },
        { text: 'Basic widget embed' },
        { text: '1 Wall of love page' },
        { text: 'Community Support' },
        { text: 'Watermarked video recording, wall of love and widgets' },
        { text: 'Custom full-page widgets', available: false },
        { text: 'Advanced email reminders', available: false },
        { text: 'Custom popup widgets', available: false }
      ]
    },
    {
      id: 'starter',
      name: 'Starter',
      monthlyPrice: 1499,
      yearlyPrice: 13999,
      description: 'For teams ready to build consistent social proof:',
      buttonClass: 'starter',
      featured: true,
      features: [
        { text: 'Everything from Basic Plan' },
        { text: 'Up to 10 video testimonials / month (1080p/5 mins)' },
        { text: 'Unlimited text & image testimonials' },
        { text: 'Custom branding (remove our logo)' },
        { text: 'Carousel + popup widgets' },
        { text: 'Automated email reminders' },
        { text: '4 Wall of love pages' },
        { text: 'Email/Chat Support' },
        { text: 'Without watermark video recording, wall of love and widgets' },
        { text: 'Zapier + Webhooks + CRM sync', available: false }
      ]
    },
    {
      id: 'growth',
      name: 'Growth',
      monthlyPrice: 3899,
      yearlyPrice: 38399,
      description: 'For agencies turning client results into repeatable growth:',
      buttonClass: 'growth',
      features: [
        { text: 'Everything from Starter Plan' },
        { text: 'Unlimited Videos (all formats, 1080p-Max size 1 GB)' },
        { text: 'Custom domain' },
        { text: 'Advanced Templates and Customization' },
        { text: 'Unlimited Wall of love pages' },
        { text: 'Priority Email Support' },
        { text: 'Without watermark video recording, wall of love and widgets' },
        { text: 'Zapier + Webhooks + CRM sync' }
      ]
    }
  ];

  const getPlanPrice = (plan) => {
    if (!plan.monthlyPrice) return null;

    return billingPeriod === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice / 12;
  };

  const formatRupees = (amount) => amount.toFixed(2);

  const faqs = [
    {
      question: 'Can I sign up with Google?',
      answer: 'Yes. Click start building trust, continue with Google, and your workspace opens quickly.'
    },
    {
      question: 'Is Clientalio free to start?',
      answer: 'Yes. You can start on the free plan today and upgrade later when customer proof becomes a bigger growth channel.'
    },
    {
      question: 'Do customers need an account to submit testimonials?',
      answer: 'No. You can share one simple link so customers can send feedback from their browser without creating an account.'
    },
    {
      question: 'Can I make customer love visible on my website?',
      answer: 'Yes. You can add approved customer proof to popular website platforms so buyers see trust signals before they contact you.'
    }
  ];

  const handleSignupClick = (location) => {
    trackSignupFree(location);
    trackStartFree(location);
    trackSignupComplete(location);
  };

  return (
    <main className="signup-page">
      <section className="signup-hero">
        <div className="signup-container signup-hero-grid">
          <div className="signup-hero-copy">
            <span className="signup-eyebrow">Start free in less than 2 minutes</span>
            <h1>Build trust before your first sales call</h1>
            <p>
              Turn happy customers into visible proof that helps new buyers trust you faster, choose you sooner, and remember your brand tomorrow.
            </p>
            <div className="signup-actions">
              <a className="signup-google-button" href={signupUrl} onClick={() => handleSignupClick('signup_hero_google')}>
                <FaGoogle aria-hidden="true" />
                Start with Google today
              </a>
              <a className="signup-secondary-button" href={signupUrl} onClick={() => handleSignupClick('signup_hero_start_free')}>
                Start building trust today <FaArrowRight aria-hidden="true" />
              </a>
            </div>
            <div className="signup-trust-row" aria-label="Signup benefits">
              <span>No credit card required</span>
              <span>Workspace ready fast</span>
              <span>Customer proof starts today</span>
            </div>
          </div>

          <div className="signup-preview" aria-label="Clientalio signup preview">
            <div className="signup-preview-header">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <div className="signup-preview-card">
              <div className="signup-preview-icon"><FaPlayCircle aria-hidden="true" /></div>
              <h2>Your first trust asset</h2>
              <p>Share one link, approve the response, and make customer love visible.</p>
              <div className="signup-preview-progress"><span></span></div>
              <ul>
                <li><FaCheckCircle aria-hidden="true" /> Branded trust page</li>
                <li><FaCheckCircle aria-hidden="true" /> Customer feedback link</li>
                <li><FaCheckCircle aria-hidden="true" /> Website-ready proof</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="signup-section signup-benefits" aria-labelledby="signup-benefits-title">
        <div className="signup-container">
          <div className="signup-section-heading">
            <span>Benefits</span>
            <h2 id="signup-benefits-title">A faster way to turn customer trust into more conversions</h2>
          </div>
          <div className="signup-benefit-grid">
            {benefits.map((benefit) => (
              <article className="signup-benefit-card" key={benefit}>
                <FaCheckCircle aria-hidden="true" />
                <p>{benefit}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="signup-section" aria-labelledby="signup-features-title">
        <div className="signup-container">
          <div className="signup-section-heading">
            <span>After signup</span>
            <h2 id="signup-features-title">In the next 2 minutes, you can start building proof buyers believe</h2>
          </div>
          <div className="signup-feature-grid">
            {features.map((feature) => (
              <article className="signup-feature-card" key={feature.title}>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="signup-section signup-testimonials" aria-labelledby="signup-testimonials-title">
        <div className="signup-container">
          <div className="signup-section-heading">
            <span>Customer proof</span>
            <h2 id="signup-testimonials-title">Stop letting valuable customer love disappear after the sale</h2>
          </div>
          <div className="signup-testimonial-grid">
            {testimonials.map((testimonial) => (
              <article className="signup-testimonial-card" key={testimonial.name}>
                <FaQuoteLeft className="quote-icon" aria-hidden="true" />
                <p>{testimonial.quote}</p>
                <div className="signup-stars" aria-label="5 star rating">
                  {[1, 2, 3, 4, 5].map((star) => <FaStar key={star} aria-hidden="true" />)}
                </div>
                <h3>{testimonial.name}</h3>
                <span>{testimonial.role}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="signup-section signup-pricing-section" aria-labelledby="signup-pricing-title">
        <div className="signup-container">
          <div className="signup-section-heading signup-pricing-heading">
            <span>Our Pricing</span>
            <h2 id="signup-pricing-title"><mark>Start free today. Upgrade when trust becomes your growth channel.</mark></h2>
          </div>

          <div className="signup-billing-toggle" aria-label="Billing period">
            <button
              type="button"
              className={billingPeriod === 'monthly' ? 'active' : ''}
              onClick={() => setBillingPeriod('monthly')}
            >
              Monthly
            </button>
            <button
              type="button"
              className={billingPeriod === 'yearly' ? 'active' : ''}
              onClick={() => setBillingPeriod('yearly')}
            >
              Annually <span>Save 22%</span>
            </button>
          </div>

          <div className="signup-pricing-grid detailed">
            {pricing.map((plan) => (
              <article className={`signup-pricing-card detailed ${plan.featured ? ' is-featured' : ''}`} key={plan.id}>
                {plan.featured && <span className="signup-plan-badge">Most Popular</span>}
                <h3>{plan.name}</h3>
                <div className="signup-price-row detailed">
                  {plan.priceLabel ? (
                    <>
                      <strong className="free-price">{plan.priceLabel}</strong>
                      <span>{plan.period}</span>
                    </>
                  ) : (
                    <>
                      <span className="currency">₹</span>
                      <strong>{formatRupees(getPlanPrice(plan))}</strong>
                      <span>/Monthly</span>
                    </>
                  )}
                </div>
                {billingPeriod === 'yearly' && plan.yearlyPrice && (
                  <p className="signup-billed-yearly">₹{formatRupees(plan.yearlyPrice)} billed annually</p>
                )}
                <p className="signup-plan-description"><mark>{plan.description}</mark></p>
                <ul>
                  {plan.features.map((feature) => (
                    <li className={feature.available === false ? 'unavailable' : ''} key={feature.text}>
                      {feature.available === false ? <FaTimes aria-hidden="true" /> : <FaCheckCircle aria-hidden="true" />}
                      {feature.text}
                    </li>
                  ))}
                </ul>
                <a
                  className={`signup-plan-button ${plan.buttonClass}`}
                  href={`${signupUrl}?plan=${plan.id}`}
                  onClick={() => handleSignupClick(`signup_pricing_${plan.id}`)}
                >
                  Start building trust today
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="signup-section signup-faqs" aria-labelledby="signup-faq-title">
        <div className="signup-container signup-faq-layout">
          <div className="signup-section-heading align-left">
            <span>FAQs</span>
            <h2 id="signup-faq-title">Questions before you start building trust?</h2>
            <p>Here are the answers teams look for before turning customer feedback into marketing assets.</p>
          </div>
          <div className="signup-faq-list">
            {faqs.map((faq, index) => {
              const isOpen = openFaq === index;

              return (
                <article className={`signup-faq-item${isOpen ? ' is-open' : ''}`} key={faq.question}>
                  <button type="button" onClick={() => setOpenFaq(isOpen ? null : index)} aria-expanded={isOpen}>
                    <span>{faq.question}</span>
                    <FaArrowRight aria-hidden="true" />
                  </button>
                  {isOpen && <p>{faq.answer}</p>}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="signup-final-cta" aria-label="Start free with Clientalio">
        <div className="signup-container">
          <h2>Ready to stop losing customer proof?</h2>
          <p>Start free with Google. In less than 2 minutes you can create your branded page, share your first link, and make customer love visible.</p>
          <a className="signup-google-button" href={signupUrl} onClick={() => handleSignupClick('signup_final_cta')}>
            Start building trust today <FaArrowRight aria-hidden="true" />
          </a>
        </div>
      </section>
    </main>
  );
}

export default Signup;